import React, { useMemo, useState } from 'react';
import { App, AutoComplete, Button, Card, Form, Input, Popconfirm, Row, Select, Switch, Table, Typography } from 'antd';
import { EditOutlined, PlusOutlined } from '@ant-design/icons';
import { useMsal } from '@azure/msal-react';
import { scopes } from '../../authConfig';
import { GetAccessToken } from '../../utils/auth-utils';
import {
  DEFAULT_SOURCE_SYSTEM,
  LostReasonDto,
  SOURCE_SYSTEMS,
  SourceSystemLostReasonDto,
} from '../../dto/lost-reason-models';
import {
  permanentDeleteSourceSystemLostReason,
  saveSourceSystemLostReason,
  setSourceSystemLostReasonActive,
} from '../../services/lost-reason-service';

const { Title, Text } = Typography;

export type SourceSystemLostReasonsTabProps = {
  reasons: SourceSystemLostReasonDto[];
  bosReasons: LostReasonDto[];
  onRefresh: () => Promise<void>;
};

export const SourceSystemLostReasonsTab: React.FC<SourceSystemLostReasonsTabProps> = ({
  reasons,
  bosReasons,
  onRefresh,
}) => {
  const { instance, inProgress } = useMsal();
  const { notification } = App.useApp();
  const [editing, setEditing] = useState<SourceSystemLostReasonDto | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [form] = Form.useForm();
  const [tablePage, setTablePage] = useState({ current: 1, pageSize: 20 });
  const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([]);
  const [filterSourceSystem, setFilterSourceSystem] = useState<string | undefined>(undefined);

  const sortedReasons = useMemo(
    () =>
      [...reasons].sort((a, b) =>
        (a.sourceSystem || '').localeCompare(b.sourceSystem || '', undefined, { sensitivity: 'base' }) ||
        a.englishLabel.localeCompare(b.englishLabel, undefined, { sensitivity: 'base' }),
      ),
    [reasons],
  );

  const filteredReasons = useMemo(() => {
    if (!filterSourceSystem) return sortedReasons;
    return sortedReasons.filter((r) => r.sourceSystem === filterSourceSystem);
  }, [sortedReasons, filterSourceSystem]);

  const sourceSystemOptions = useMemo(() => {
    const existingSystems = Array.from(new Set(reasons.map((r) => r.sourceSystem).filter(Boolean)));
    const allSystems = Array.from(new Set([...SOURCE_SYSTEMS, ...existingSystems])).sort();
    return allSystems.map((s) => ({ value: s, label: s }));
  }, [reasons]);

  const bosReasonOptions = useMemo(() => {
    const byId = new Map(bosReasons.map((r) => [r.id, r]));
    let pool = bosReasons.filter((r) => r.isActive);
    const linkedId = editing?.lostReasonId;
    if (linkedId && !pool.some((r) => r.id === linkedId)) {
      const linked = byId.get(linkedId);
      if (linked) pool = [...pool, linked];
    }
    return pool
      .map((r) => ({
        value: r.id!,
        label: `${r.englishLabel} / ${r.germanLabel}${r.isActive ? '' : ' (inactive)'}`,
      }))
      .filter((o) => o.value);
  }, [bosReasons, editing?.lostReasonId]);

  const closeForm = () => {
    setShowForm(false);
    setEditing(null);
    setExpandedRowKeys([]);
  };

  const openNew = () => {
    setExpandedRowKeys([]);
    const firstBos = bosReasonOptions[0]?.value ?? '';
    setEditing({
      id: '',
      lostReasonId: typeof firstBos === 'string' ? firstBos : String(firstBos),
      sourceSystem: DEFAULT_SOURCE_SYSTEM,
      englishLabel: '',
      germanLabel: '',
      isActive: true,
    });
    form.setFieldsValue({
      lostReasonId: firstBos || undefined,
      sourceSystem: DEFAULT_SOURCE_SYSTEM,
      englishLabel: '',
      germanLabel: '',
      isActive: true,
    });
    setShowForm(true);
  };

  const openEdit = (r: SourceSystemLostReasonDto) => {
    if (!r.id) return;
    const idx = sortedReasons.findIndex((x) => x.id === r.id);
    if (idx >= 0) {
      const page = Math.floor(idx / tablePage.pageSize) + 1;
      setTablePage((p) => ({ ...p, current: page }));
    }
    setExpandedRowKeys([r.id]);
    setEditing({ ...r });
    form.setFieldsValue({
      lostReasonId: r.lostReasonId,
      sourceSystem: r.sourceSystem || DEFAULT_SOURCE_SYSTEM,
      englishLabel: r.englishLabel,
      germanLabel: r.germanLabel,
      isActive: r.isActive,
    });
    setShowForm(true);
  };

  const onSubmit = async () => {
    try {
      const values = await form.validateFields();
      const token = await GetAccessToken(instance, inProgress, scopes.salesAppointmentApi);
      const res = await saveSourceSystemLostReason(token.accessToken, {
        id: editing?.id || null,
        lostReasonId: String(values.lostReasonId),
        sourceSystem: values.sourceSystem || DEFAULT_SOURCE_SYSTEM,
        englishLabel: values.englishLabel,
        germanLabel: values.germanLabel,
        isActive: values.isActive,
      });
      if (res.isSuccess) {
        notification.success({ message: 'Source system lost reason saved' });
        closeForm();
        await onRefresh();
      } else {
        notification.error({ message: res.errors?.join(', ') ?? 'Save failed' });
      }
    } catch {
    }
  };

  const onDeactivate = async (r: SourceSystemLostReasonDto) => {
    if (!r.id) {
      notification.error({ message: 'Cannot deactivate an unsaved source system lost reason.' });
      return;
    }
    const token = await GetAccessToken(instance, inProgress, scopes.salesAppointmentApi);
    const res = await setSourceSystemLostReasonActive(token.accessToken, r.id, false);
    if (res.isSuccess) {
      notification.success({ message: 'Source system lost reason deactivated' });
      await onRefresh();
    } else {
      notification.error({ message: (res as any).errors?.join?.(', ') ?? 'Deactivate failed' });
    }
  };

  const onPermanentDelete = async (r: SourceSystemLostReasonDto) => {
    if (!r.id) {
      notification.error({ message: 'Cannot delete an unsaved source system lost reason.' });
      return;
    }
    const token = await GetAccessToken(instance, inProgress, scopes.salesAppointmentApi);
    const res = await permanentDeleteSourceSystemLostReason(token.accessToken, r.id);
    if (res.isSuccess) {
      notification.success({ message: 'Source system lost reason deleted permanently' });
      await onRefresh();
    } else {
      notification.error({ message: (res as any).errors?.join?.(', ') ?? 'Delete failed' });
    }
  };

  const mappingFormItems = (
    <>
      <Form.Item name="lostReasonId" label="BOS lost reason" rules={[{ required: true, message: 'Select a BOS reason' }]}>
        <Select
          showSearch
          optionFilterProp="label"
          options={bosReasonOptions}
          placeholder="Map onto one BOS reason"
          disabled={bosReasonOptions.length === 0}
        />
      </Form.Item>
      <Form.Item name="sourceSystem" label="Source system" rules={[{ required: true }]}>
        <AutoComplete
          options={sourceSystemOptions}
          placeholder="Type or select a source system"
          filterOption={(inputValue, option) =>
            option!.value.toUpperCase().indexOf(inputValue.toUpperCase()) !== -1
          }
        />
      </Form.Item>
      <Form.Item name="englishLabel" label="English (Source system)" rules={[{ required: true }]}>
        <Input placeholder="Value stored on case / sent to source system" />
      </Form.Item>
      <Form.Item name="germanLabel" label="German (Source system)" rules={[{ required: true }]}>
        <Input />
      </Form.Item>
      <Form.Item name="isActive" label="Active" valuePropName="checked">
        <Switch />
      </Form.Item>
      <Button type="primary" onClick={() => void onSubmit()} disabled={bosReasonOptions.length === 0}>
        Save
      </Button>
      <Button style={{ marginLeft: 8 }} onClick={() => closeForm()}>
        Cancel
      </Button>
    </>
  );

  const columns = [
    {
      title: '#',
      key: 'seq',
      width: 52,
      render: (_: unknown, __: SourceSystemLostReasonDto, index: number) =>
        (tablePage.current - 1) * tablePage.pageSize + index + 1,
    },
    { title: 'System', dataIndex: 'sourceSystem', key: 'system', width: 120 },
    {
      title: 'BOS reason',
      key: 'bos',
      ellipsis: true,
      render: (_: unknown, r: SourceSystemLostReasonDto) =>
        r.lostReasonEnglishLabel || r.lostReasonGermanLabel
          ? `${r.lostReasonEnglishLabel ?? '—'} / ${r.lostReasonGermanLabel ?? '—'}`
          : '—',
    },
    { title: 'EN (Source system)', dataIndex: 'englishLabel', key: 'en' },
    { title: 'DE (Source system)', dataIndex: 'germanLabel', key: 'de' },
    {
      title: 'Active',
      dataIndex: 'isActive',
      key: 'active',
      width: 80,
      render: (v: boolean) => (
        <Text
          type={v ? 'success' : undefined}
          strong
          style={v ? undefined : { color: 'var(--ant-color-text-disabled)' }}
        >
          {v ? 'Yes' : 'No'}
        </Text>
      ),
    },
    {
      title: '',
      key: 'a',
      width: 320,
      render: (_: unknown, r: SourceSystemLostReasonDto) => (
        <>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)}>
            Edit
          </Button>
          {r.isActive && (
            <Button size="small" style={{ marginLeft: 8 }} onClick={() => void onDeactivate(r)}>
              Deactivate
            </Button>
          )}
          <Popconfirm
            title="Delete permanently?"
            description="Removes this source system mapping. The BOS reason itself is not affected."
            okText="Delete"
            cancelText="Cancel"
            okButtonProps={{ danger: true }}
            onConfirm={() => void onPermanentDelete(r)}
          >
            <Button size="small" danger style={{ marginLeft: 8 }}>
              Delete
            </Button>
          </Popconfirm>
        </>
      ),
    },
  ];

  return (
    <div
      className="container flex-admin-vertical-container"
      style={{ width: '100%', maxWidth: 1200, minWidth: 0, justifyContent: 'flex-start' }}
    >
      <Row style={{ marginBottom: 8, flexDirection: 'column' }}>
        <Title level={4} style={{ marginBottom: 0 }}>
          Source system lost reasons
        </Title>
        <Text type="secondary">
          Per-system labels used for reporting and case completion (customerLostReasons). Each row maps one source
          system (e.g. Salesforce) onto a single BOS reason, so several systems can point to the same BOS reason.
        </Text>
      </Row>

      {bosReasonOptions.length === 0 && (
        <Text type="warning" style={{ display: 'block', marginBottom: 16 }}>
          Add at least one active BOS lost reason before creating source system mappings.
        </Text>
      )}

      {showForm && editing && !editing.id ? (
        <Card style={{ marginBottom: 24 }}>
          <Title level={5}>New source system lost reason</Title>
          <Form form={form} layout="vertical" style={{ maxWidth: 480 }}>
            {mappingFormItems}
          </Form>
        </Card>
      ) : null}

      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Select
          allowClear
          showSearch
          placeholder="Filter by source system"
          style={{ width: 250 }}
          value={filterSourceSystem}
          onChange={(value) => {
            setFilterSourceSystem(value);
            setTablePage((p) => ({ ...p, current: 1 }));
          }}
          options={[
            ...sourceSystemOptions.map((opt) => ({
              value: opt.value,
              label: opt.label,
            })),
          ]}
        />
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={openNew}
          disabled={bosReasonOptions.length === 0 || (showForm && !editing?.id)}
        >
          Add source system reason
        </Button>
      </Row>

      <Table
        rowKey="id"
        size="small"
        columns={columns}
        dataSource={filteredReasons}
        pagination={{
          current: tablePage.current,
          pageSize: tablePage.pageSize,
          showSizeChanger: true,
          onChange: (page, pageSize) => {
            setTablePage({ current: page, pageSize: pageSize ?? 20 });
            setExpandedRowKeys([]);
            if (editing?.id) {
              setShowForm(false);
              setEditing(null);
            }
          },
        }}
        expandable={{
          showExpandColumn: false,
          expandedRowKeys,
          onExpandedRowsChange: (keys) => setExpandedRowKeys(keys as string[]),
          expandedRowRender: (record) => {
            if (!editing?.id || record.id !== editing.id) return null;
            return (
              <Card size="small" style={{ marginBottom: 8 }} styles={{ body: { paddingBottom: 12 } }}>
                <Title level={5} style={{ marginTop: 0 }}>
                  Edit source system lost reason
                </Title>
                <Form form={form} layout="vertical" style={{ maxWidth: 480 }}>
                  {mappingFormItems}
                </Form>
              </Card>
            );
          },
        }}
      />
    </div>
  );
};
